import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { selectBillData, selectLoading, selectFromDate, selectToDate, selectSelectedBranch, fetchBillsData } from '../../store/slices/billReportsSlice';
import { cancelBill, updateBillPaymentMode, getReceiptDetails, settleCreditBill } from '../../utilities/api';
import { exportStyledExcel } from '../../utilities/excelExport';
import { normalizeMop, isCancelledStatus, isCreditMop, buildBranchAnalysisRows } from '../../utilities/branchAnalysis';
import './billreports.css';

/** Same mode of payment across all rows (for cancel & change MOP). */
const validateUniformMop = (rows) => {
    if (!rows.length) return { ok: false, message: 'Select at least one bill.' };
    const first = normalizeMop(rows[0].final_mop);
    if (!first) return { ok: false, message: 'Selected bills are missing mode of payment.' };
    const mismatch = rows.find((r) => normalizeMop(r.final_mop) !== first);
    if (mismatch) {
        return {
            ok: false,
            message: 'All selected bills must have the same mode of payment for this action.'
        };
    }
    return { ok: true, mop: rows[0].final_mop };
};

const validateSettlementSelection = (rows) => {
    if (!rows.length) return { ok: false, message: 'Select at least one bill.' };
    const nonCredit = rows.filter((r) => !isCreditMop(r));
    if (nonCredit.length) {
        return {
            ok: false,
            message: 'Settlement is only allowed for bills with mode of payment Credit.'
        };
    }
    const cancelled = rows.filter(isCancelledStatus);
    if (cancelled.length) {
        return { ok: false, message: 'Remove cancelled bills from the selection.' };
    }
    return { ok: true };
};

const validateCancelOrChangeMop = (rows) => {
    const base = validateUniformMop(rows);
    if (!base.ok) return base;
    const cancelled = rows.filter(isCancelledStatus);
    if (cancelled.length) {
        return { ok: false, message: 'Cancelled bills cannot be included for this action.' };
    }
    return { ok: true, mop: base.mop };
};

const getBranchIdForRow = (row, selectedBranch) => row.branch_id || selectedBranch?.branch_id || localStorage.getItem('branchid') || '';

const validateSameBranch = (rows, selectedBranch) => {
    const ids = rows.map((r) => getBranchIdForRow(r, selectedBranch)).filter(Boolean);
    if (!ids.length) {
        return { ok: false, message: 'Could not resolve branch for selected bill(s). Select a branch in the filter or ensure bills include branch information.' };
    }
    const first = String(ids[0]);
    if (ids.some((id) => String(id) !== first)) {
        return {
            ok: false,
            message: 'For this action, select bills from the same branch only (payment options are loaded per branch).'
        };
    }
    return { ok: true, branchId: first };
};

/** Branch must be chosen in the report header dropdown (not cleared, not "All"). */
const isBranchChosenInFilter = (selectedBranch) => {
    if (selectedBranch == null) return false;
    const id = selectedBranch.branch_id;
    if (id === undefined || id === null) return false;
    const s = String(id).trim();
    if (!s) return false;
    if (s.toLowerCase() === 'all') return false;
    return true;
};

const getStoredBranchId = () => {
    const id = localStorage.getItem('branchid');
    if (id === undefined || id === null) return '';
    const s = String(id).trim();
    if (!s || s.toLowerCase() === 'all') return '';
    return s;
};

/** Filter branch, or localStorage branch for manager/admin when filter is unset or "All". */
const resolveActionBranchId = (selectedBranch, { isManager, isAdmin } = {}) => {
    if (isBranchChosenInFilter(selectedBranch)) {
        return String(selectedBranch.branch_id).trim();
    }
    if (isManager || isAdmin) {
        return getStoredBranchId();
    }
    return '';
};

const parseReceiptPayload = (data) => {
    let d = data;
    if (typeof d === 'string') {
        try {
            d = JSON.parse(d);
        } catch {
            return null;
        }
    }
    if (d?.final_output) {
        try {
            d = JSON.parse(d.final_output);
        } catch {
            return null;
        }
    }
    return d;
};

/** Payment modes from receipt_settings excluding current (for change MOP). */
const getAlternatePaymentOptions = (receiptSettings, currentPaymentMode) => {
    if (!receiptSettings || !Array.isArray(receiptSettings)) return [];
    const current = normalizeMop(currentPaymentMode);
    return receiptSettings
        .filter((s) => (s.setting_type === 'Payment' || (s.setting_type && String(s.setting_type).toLowerCase() === 'payment')) && String(s.setting_access) === '1')
        .filter((s) => {
            const name = normalizeMop(s.setting_name || s.payment_name || s.setting_value);
            return name && name !== current;
        })
        .map((s) => ({
            label: s.setting_name || s.payment_name || s.setting_value || 'Payment',
            value: s.setting_name || s.payment_name || s.setting_value
        }));
};

/** Payment modes for settlement (non-credit). */
const getSettlementPaymentOptions = (receiptSettings) => {
    if (!receiptSettings || !Array.isArray(receiptSettings)) return [];
    return receiptSettings
        .filter((s) => (s.setting_type === 'Payment' || (s.setting_type && String(s.setting_type).toLowerCase() === 'payment')) && String(s.setting_access) === '1')
        .filter((s) => {
            const name = normalizeMop(s.setting_name || s.payment_name || s.setting_value);
            return name && name !== 'CREDIT' && !name.includes('CREDIT');
        })
        .map((s) => ({
            label: s.setting_name || s.payment_name || s.setting_value || 'Payment',
            value: s.setting_name || s.payment_name || s.setting_value
        }));
};

const isSettleSuccess = (data) => {
    const row = Array.isArray(data) ? data[0] : data;
    return String(row?.status || '').toLowerCase() === 'success';
};

const cancelReasonOptions = [
    { label: 'Duplicate Item', value: 'duplicate item' },
    { label: 'Wrong Item Name', value: 'wrong item name' },
    { label: 'Item Not Available', value: 'item not available' }
];

const BillReportsContent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useRef(null);

    const billData = useSelector(selectBillData);
    const loading = useSelector(selectLoading);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);

    const rolename = localStorage.getItem('role') || '';
    const isCEO = rolename.toUpperCase() === 'CEO';
    const isAdmin = rolename.toUpperCase() === 'ADMIN';
    const isManager = rolename.toUpperCase() === 'MANAGER';
    const canExport = isCEO || isAdmin;
    const editedBy = localStorage.getItem('userid') || '';

    const [filteredBillData, setFilteredBillData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);

    const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
    const [cancelRemarks, setCancelRemarks] = useState('');
    const [cancelSubmitting, setCancelSubmitting] = useState(false);

    const [changeMopDialogVisible, setChangeMopDialogVisible] = useState(false);
    const [changeMopOptions, setChangeMopOptions] = useState([]);
    const [selectedNewMop, setSelectedNewMop] = useState(null);
    const [changeMopRemarks, setChangeMopRemarks] = useState('');
    const [changeMopLoading, setChangeMopLoading] = useState(false);
    const [changeMopSubmitting, setChangeMopSubmitting] = useState(false);

    const [settlementDialogVisible, setSettlementDialogVisible] = useState(false);
    const [settlementPaymentOptions, setSettlementPaymentOptions] = useState([]);
    const [settlementPaymentType, setSettlementPaymentType] = useState(null);
    const [settlementDate, setSettlementDate] = useState(null);
    const [settlementRemarks, setSettlementRemarks] = useState('');
    const [settlementLoading, setSettlementLoading] = useState(false);
    const [settlementSubmitting, setSettlementSubmitting] = useState(false);

    useEffect(() => {
        setFilteredBillData(billData);
        setGlobalFilter('');
        setSelectedStatusFilter(null);
        setSelectedRows([]);
    }, [billData]);

    const statusFilterOptions = React.useMemo(() => {
        const statuses = Array.from(new Set((billData || []).map((b) => (b.status || '').toString().trim()).filter(Boolean)));
        const normalized = statuses.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return [{ label: 'All Statuses', value: null }, ...normalized.map((status) => ({ label: status.charAt(0).toUpperCase() + status.slice(1), value: status }))];
    }, [billData]);

    const tableRows = useMemo(
        () =>
            filteredBillData.map((row) => ({
                ...row,
                __selectionKey: `${String(row.bill_id)}__${String(getBranchIdForRow(row, selectedBranch))}`
            })),
        [filteredBillData, selectedBranch]
    );

    useEffect(() => {
        setSelectedRows((prev) => {
            if (!prev.length) return prev;
            const byKey = new Map(tableRows.map((r) => [r.__selectionKey, r]));
            const next = prev.map((r) => byKey.get(r.__selectionKey)).filter(Boolean);
            return next;
        });
    }, [tableRows]);

    const refetchBills = useCallback(() => {
        const to = toDate ? new Date(toDate) : new Date();
        const from = fromDate
            ? new Date(fromDate)
            : (() => {
                  const d = new Date();
                  if (!isManager) {
                      d.setDate(d.getDate() - 30);
                  }
                  return d;
              })();
        // Reload exactly the scope the filter shows ("All" included), not the
        // narrower branch that bill actions are resolved against.
        const branchId = selectedBranch?.branch_id || resolveActionBranchId(selectedBranch, { isManager, isAdmin }) || 'All';
        dispatch(
            fetchBillsData({
                fromDate: from,
                toDate: to,
                branchId,
                status: ''
            })
        );
    }, [dispatch, fromDate, toDate, selectedBranch, isManager, isAdmin]);

    const formatDate = (dateString) => {
        return moment(dateString).isValid() ? moment(dateString).format('DD-MM-YYYY') : '';
    };

    /**
     * '0', '0000-00-00' etc. are the sentinel this DB uses for "not set" on several date
     * columns (same convention as stlmnt_date/branch_id elsewhere) rather than NULL. moment's
     * loose fallback parser doesn't reject these — moment('0') resolves to a bogus real date
     * instead of "invalid" — so they must be screened out before ever reaching moment.
     */
    const isBlankDateValue = (value) => {
        if (value === null || value === undefined) return true;
        const s = String(value).trim();
        return s === '' || /^0+([-:/ ]0+)*$/.test(s);
    };

    /** created/updated are single DATETIME columns (unlike invoice_date/invoice_time, which are separate) — split one value into { date, time }. */
    const formatDateTime = (value) => {
        if (isBlankDateValue(value)) return { date: '-', time: '' };
        const m = moment(value);
        if (!m.isValid()) return { date: '-', time: '' };
        return { date: m.format('DD-MM-YYYY'), time: m.format('hh:mm A') };
    };

    /**
     * Pairs a separate date + time column (e.g. invoice_date/invoice_time, edit_date/edit_time)
     * into { date, time }, both normalized to DD-MM-YYYY / 12-hour time regardless of how the
     * source column stored it (invoice_time is "hh:mm A" already; edit_time is 24-hour "HH:mm:ss").
     */
    const combineDateTime = (dateStr, timeStr) => {
        if (isBlankDateValue(dateStr) || !moment(dateStr).isValid()) return { date: '-', time: '' };
        const date = moment(dateStr).format('DD-MM-YYYY');
        if (isBlankDateValue(timeStr)) return { date, time: '' };
        const t = moment(timeStr, ['h:mm A', 'H:mm:ss', 'H:mm'], true);
        return { date, time: t.isValid() ? t.format('hh:mm A') : timeStr };
    };

    /**
     * created/updated come back NULL on rows from branch tables/legacy data that never
     * had those columns populated. Rather than show a blank cell, fall back to fields we
     * know are reliably set: invoice_date/invoice_time (when the bill was made) for
     * "Created", and edit_date/edit_time (set on cancel/MOP-change/settlement) for
     * "Updated" — falling further back to invoice_date/invoice_time if the bill was never edited.
     */
    const getCreatedDateTime = (row) => {
        const dt = formatDateTime(row?.created);
        return dt.date !== '-' ? dt : combineDateTime(row?.invoice_date, row?.invoice_time);
    };

    const getUpdatedDateTime = (row) => {
        const dt = formatDateTime(row?.updated);
        if (dt.date !== '-') return dt;
        const edited = combineDateTime(row?.edit_date, row?.edit_time);
        return edited.date !== '-' ? edited : combineDateTime(row?.invoice_date, row?.invoice_time);
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    const statusBodyTemplate = (rowData) => {
        const status = rowData.status?.toLowerCase();
        if (status === 'pending') {
            return <Tag value="Pending" severity="warning" />;
        } else if (status === 'closed') {
            return <Tag value="Closed" severity="success" />;
        } else if (status === 'settled') {
            return <Tag value="Settled" severity="success" />;
        } else if (status === 'cancelled') {
            return <Tag value="Cancelled" severity="danger" />;
        } else {
            return <Tag value={status || 'N/A'} severity="info" />;
        }
    };

    const viewBill = (rowData) => {
        if (rowData.bill_id) {
            let branchId = rowData.branch_id || selectedBranch?.branch_id || '';

            if (!branchId && (isManager || isAdmin)) {
                branchId = localStorage.getItem('branchid') || '';
            }

            if (!branchId) {
                alert('Please select a branch in the filter to view the receipt.');
                return;
            }
            navigate(`/billreceipt/${rowData.bill_id}/${branchId}`, {
                state: { userId: rowData.userId ?? rowData.user_id }
            });
        }
    };

    const actionBodyTemplate = (rowData) => {
        return <Button label="View" icon="pi pi-eye" className="p-button-sm p-button-info" onClick={() => viewBill(rowData)} />;
    };

    const openCancelDialog = () => {
        const v = validateCancelOrChangeMop(selectedRows);
        if (!v.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Cannot cancel', detail: v.message, life: 5000 });
            return;
        }
        setCancelRemarks('');
        setCancelDialogVisible(true);
    };

    const submitCancelBills = async () => {
        if (!cancelRemarks) {
            toast.current?.show({ severity: 'warn', summary: 'Reason required', detail: 'Please select a reason for cancellation.', life: 4000 });
            return;
        }
        if (!editedBy) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'User id not found. Please log in again.', life: 4000 });
            return;
        }
        setCancelSubmitting(true);
        try {
            for (const row of selectedRows) {
                const branchId = getBranchIdForRow(row, selectedBranch);
                if (!branchId) {
                    throw new Error(`Branch is missing for bill ${row.bill_id}.`);
                }
                await cancelBill(row.bill_id, branchId, cancelRemarks, editedBy);
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Cancelled',
                detail: `${selectedRows.length} bill(s) cancelled successfully.`,
                life: 4000
            });
            setCancelDialogVisible(false);
            setCancelRemarks('');
            setSelectedRows([]);
            refetchBills();
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Cancel failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to cancel bill(s)',
                life: 5000
            });
        } finally {
            setCancelSubmitting(false);
        }
    };

    const openChangeMopDialog = async () => {
        const v = validateCancelOrChangeMop(selectedRows);
        if (!v.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Cannot change payment', detail: v.message, life: 5000 });
            return;
        }
        const branchCheck = validateSameBranch(selectedRows, selectedBranch);
        if (!branchCheck.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Branch', detail: branchCheck.message, life: 5000 });
            return;
        }
        const first = selectedRows[0];
        const branchId = branchCheck.branchId;
        setSelectedNewMop(null);
        setChangeMopRemarks('');
        setChangeMopOptions([]);
        setChangeMopDialogVisible(true);
        setChangeMopLoading(true);
        try {
            let data = await getReceiptDetails(first.bill_id, branchId);
            const parsed = parseReceiptPayload(data);
            const settings = parsed?.receipt_settings || [];
            const options = getAlternatePaymentOptions(settings, first.final_mop);
            setChangeMopOptions(options);
            if (!options.length) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'No other payment modes',
                    detail: 'No alternate payment modes are available from receipt settings.',
                    life: 5000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Load failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to load payment modes',
                life: 5000
            });
            setChangeMopDialogVisible(false);
        } finally {
            setChangeMopLoading(false);
        }
    };

    const submitChangeMop = async () => {
        if (!selectedNewMop) {
            toast.current?.show({ severity: 'warn', summary: 'Select payment', detail: 'Please select a new mode of payment.', life: 4000 });
            return;
        }
        if (!changeMopRemarks.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Remarks required', detail: 'Please enter remarks for this payment mode change.', life: 4000 });
            return;
        }
        if (!editedBy) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'User id not found. Please log in again.', life: 4000 });
            return;
        }
        setChangeMopSubmitting(true);
        try {
            for (const row of selectedRows) {
                const branchId = getBranchIdForRow(row, selectedBranch);
                if (!branchId) throw new Error(`Branch is missing for bill ${row.bill_id}.`);
                await updateBillPaymentMode(row.bill_id, branchId, selectedNewMop, editedBy, changeMopRemarks.trim());
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Updated',
                detail: `Payment mode updated for ${selectedRows.length} bill(s).`,
                life: 4000
            });
            setChangeMopDialogVisible(false);
            setSelectedNewMop(null);
            setChangeMopRemarks('');
            setSelectedRows([]);
            refetchBills();
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Update failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to update payment mode',
                life: 5000
            });
        } finally {
            setChangeMopSubmitting(false);
        }
    };

    const openSettlementDialog = async () => {
        const v = validateSettlementSelection(selectedRows);
        if (!v.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Settlement', detail: v.message, life: 5000 });
            return;
        }
        const branchCheck = validateSameBranch(selectedRows, selectedBranch);
        if (!branchCheck.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Branch', detail: branchCheck.message, life: 5000 });
            return;
        }
        const first = selectedRows[0];
        const branchId = branchCheck.branchId;
        setSettlementPaymentType(null);
        setSettlementDate(null);
        setSettlementRemarks('');
        setSettlementPaymentOptions([]);
        setSettlementDialogVisible(true);
        setSettlementLoading(true);
        try {
            let data = await getReceiptDetails(first.bill_id, branchId);
            const parsed = parseReceiptPayload(data);
            const settings = parsed?.receipt_settings || [];
            const options = getSettlementPaymentOptions(settings);
            setSettlementPaymentOptions(options);
            if (!options.length) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'No payment modes',
                    detail: 'No non-credit payment modes found in receipt settings.',
                    life: 5000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Load failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to load settlement options',
                life: 5000
            });
            setSettlementDialogVisible(false);
        } finally {
            setSettlementLoading(false);
        }
    };

    const submitSettlement = async () => {
        if (!settlementPaymentType) {
            toast.current?.show({ severity: 'warn', summary: 'Payment type', detail: 'Please select a payment type.', life: 4000 });
            return;
        }
        if (!settlementDate) {
            toast.current?.show({ severity: 'warn', summary: 'Date', detail: 'Please select settlement date.', life: 4000 });
            return;
        }
        if (!settlementRemarks.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Remarks required', detail: 'Please enter remarks for this settlement.', life: 4000 });
            return;
        }
        const settlementBy = editedBy;
        if (!settlementBy) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'User id not found. Please log in again.', life: 4000 });
            return;
        }
        setSettlementSubmitting(true);
        const dateStr = moment(settlementDate).format('YYYY-MM-DD');
        try {
            for (const row of selectedRows) {
                const branchId = getBranchIdForRow(row, selectedBranch);
                if (!branchId) throw new Error(`Branch is missing for bill ${row.bill_id}.`);
                const amount = String(row.total_paid_amount ?? row.balance ?? '');
                const payload = {
                    bill_id: row.bill_id,
                    branch_id: branchId,
                    payment_type: settlementPaymentType,
                    amount,
                    remarks: settlementRemarks.trim(),
                    settlement_date: dateStr,
                    settlement_by: settlementBy
                };
                const res = await settleCreditBill(payload);
                if (!isSettleSuccess(res)) {
                    throw new Error(typeof res === 'object' ? JSON.stringify(res) : 'Settlement was not successful');
                }
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Settled',
                detail: `${selectedRows.length} bill(s) settled successfully.`,
                life: 4000
            });
            setSettlementDialogVisible(false);
            setSettlementRemarks('');
            setSelectedRows([]);
            refetchBills();
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Settlement failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to settle bill(s)',
                life: 5000
            });
        } finally {
            setSettlementSubmitting(false);
        }
    };

    /** Rows to export: the checked selection when there is one, otherwise every row matching the current search/status filter. */
    const getExportRows = () => (selectedRows.length ? selectedRows : filteredBillData);

    /**
     * Chronological start/end invoice_no of the currently exported rows — Starting = the
     * earliest bill, Ending = the most recent. Rows come from the backend already ordered
     * `invoice_date DESC, bill_id DESC` (most recent first), so that's the *last* row and
     * the *first* row respectively, unless a manual checkbox selection (whose order is
     * click order, not chronological) is in play.
     */
    const getInvoiceRange = () => {
        const rows = getExportRows();
        if (!rows.length) return { start: '', end: '' };
        return {
            start: rows[rows.length - 1]?.invoice_no || '',
            end: rows[0]?.invoice_no || ''
        };
    };

    /**
     * Organization name (branch_creation) shown on exports — same single name whether one
     * branch or "All Branches" is selected. Shows org_name and branch_title together when
     * both are set (e.g. "Gururaya Bakery Pvt Ltd / Gururaya Bakery - MG Road"); falls back
     * to whichever one is present when only one is, and further to the branch name (always
     * populated) when a branch has neither org_name nor branch_title filled in — otherwise
     * the export header would silently show nothing at all.
     */
    const getOrganizationName = () => {
        const rows = getExportRows();
        if (!rows.length) return '';
        const found = rows.find((b) => (b.org_name || '').trim() || (b.branch_title || '').trim()) || rows[0];
        const orgName = (found.org_name || '').trim();
        const branchTitle = (found.branch_title || '').trim();
        if (orgName && branchTitle && orgName !== branchTitle) return `${orgName} / ${branchTitle}`;
        return orgName || branchTitle || (found.branch || '').trim();
    };

    const buildPdfDoc = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const centerX = pageWidth / 2;
        const organizationName = getOrganizationName();
        const invoiceRange = getInvoiceRange();

        let yPos = 15;
        if (organizationName) {
            doc.setFontSize(15);
            doc.setFont(undefined, 'bold');
            doc.text(organizationName, centerX, yPos, { align: 'center' });
            yPos += 8;
        }

        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text('Bill Report', centerX, yPos, { align: 'center' });
        yPos += 6;

        doc.setDrawColor(200);
        doc.line(14, yPos, pageWidth - 14, yPos);
        yPos += 6;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const leftX = 14;
        const rightX = pageWidth - 14;

        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, leftX, yPos);
        }
        doc.text(`Branch: ${selectedBranch?.branch_name || 'All Branches'}`, rightX, yPos, { align: 'right' });
        yPos += 6;

        if (invoiceRange.start || invoiceRange.end) {
            doc.text(`Invoice No: ${invoiceRange.start || '-'} to ${invoiceRange.end || '-'}`, leftX, yPos);
        }
        doc.text(`Generated On: ${moment().format('DD-MM-YYYY HH:mm')}`, rightX, yPos, { align: 'right' });
        yPos += 4;

        // Created/Updated are left out of every export (Excel/PDF/Print) — they're
        // an on-screen-only audit detail, not part of the printed/shared report.
        const exportRows = getExportRows();
        const exportDiscountSummary = computeDiscountSummary(exportRows);
        const exportNcSummary = computeNcSummary(exportRows);

        if (exportDiscountSummary.count > 0) {
            doc.text(`Discount: Rs.${formatCurrency(exportDiscountSummary.amount)} (${exportDiscountSummary.count} bills)`, leftX, yPos);
        }
        if (exportNcSummary.count > 0) {
            doc.text(`NC: Rs.${formatCurrency(exportNcSummary.amount)} (${exportNcSummary.count} bills)`, rightX, yPos, { align: 'right' });
        }
        if (exportDiscountSummary.count > 0 || exportNcSummary.count > 0) yPos += 4;

        const head = ['Invoice No', 'Billed By', 'Date', 'Time', 'Basic'];
        if (exportDiscountSummary.count > 0) head.push('Discount');
        head.push('Taxable', 'Tax', 'Total', 'MOP', 'Status');

        const tableBody = exportRows.map((b) => {
            const row = [b.invoice_no || '', getBilledByName(b), formatDate(b.invoice_date), b.invoice_time || '', formatCurrency(b.total_basic)];
            if (exportDiscountSummary.count > 0) row.push(formatCurrency(b.discount));
            row.push(formatCurrency(b.taxable), formatCurrency(b.tax_amount), formatCurrency(b.total_paid_amount), b.final_mop || '', b.status || '');
            return row;
        });

        // Reconciliation coloring targets Taxable/Total by column index, which shifts by
        // one when Discount is/isn't included — compute the indices from the actual header.
        const TAXABLE_COL_INDEX = head.indexOf('Taxable');
        const TOTAL_COL_INDEX = head.indexOf('Total');

        if (isCEO) {
            const grandTotal = exportRows.reduce((acc, curr) => acc + parseFloat(curr.total_paid_amount || 0), 0);
            const footerRow = new Array(head.length).fill('');
            footerRow[TOTAL_COL_INDEX - 1] = 'Grand Total';
            footerRow[TOTAL_COL_INDEX] = formatCurrency(grandTotal);
            tableBody.push(footerRow);
        }

        autoTable(doc, {
            startY: yPos + 4,
            head: [head],
            body: tableBody,
            // 'grid' draws a full border around every cell (head, body, and foot) instead of
            // the default striped theme's borderless rows, so each column reads cleanly.
            theme: 'grid',
            styles: { fontSize: 8, lineWidth: 0.1, lineColor: [180, 180, 180] },
            headStyles: { fillColor: [74, 58, 167], textColor: 255, fontStyle: 'bold', halign: 'center' },
            footStyles: { fontStyle: 'bold' },
            // Color Taxable/Total red when they don't reconcile against Basic/Discount/Tax,
            // green when they do — same check as the on-screen DataTable.
            didParseCell: (data) => {
                if (data.section !== 'body') return;
                if (data.column.index !== TAXABLE_COL_INDEX && data.column.index !== TOTAL_COL_INDEX) return;
                const row = exportRows[data.row.index];
                if (!row) return;
                const ok = data.column.index === TAXABLE_COL_INDEX ? isTaxableCorrect(row) : isTotalCorrect(row);
                data.cell.styles.textColor = ok ? [22, 163, 74] : [220, 38, 38];
                data.cell.styles.fontStyle = 'bold';
            }
        });

        return doc;
    };

    const exportToPDF = () => {
        if (filteredBillData.length === 0) {
            return;
        }
        const doc = buildPdfDoc();
        doc.save(`BillReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    const printReport = () => {
        if (filteredBillData.length === 0) {
            return;
        }
        const doc = buildPdfDoc();
        doc.autoPrint();
        window.open(doc.output('bloburl'), '_blank');
    };

    const exportToExcel = async () => {
        if (filteredBillData.length === 0) {
            return;
        }

        const HIGHLIGHT_FILL = 'FFEDEBFA';
        const organizationName = getOrganizationName();
        const invoiceRange = getInvoiceRange();
        const exportRows = getExportRows();
        const exportDiscountSummary = computeDiscountSummary(exportRows);
        const exportNcSummary = computeNcSummary(exportRows);

        // Same identify -> who -> when -> money -> status ordering as the on-screen table.
        // ('Synch Date'/'Synch Time' were dropped — filtter_billdetails never selects those
        // fields, so they always exported blank.)
        const billColumns = [
            { header: 'Bill ID', getValue: (b) => b.bill_id || '-' },
            { header: 'Invoice No', getValue: (b) => b.invoice_no || '-' },
            { header: 'Customer', getValue: (b) => getCustomerDisplay(b) },
            { header: 'Billed By', getValue: (b) => getBilledByName(b) },
            { header: 'Invoice Date', getValue: (b) => formatDate(b.invoice_date) },
            { header: 'Invoice Time', getValue: (b) => b.invoice_time || '-' },
            { header: 'Basic Amount', getValue: (b) => parseFloat(b.total_basic || 0), numeric: true },
            // Discount column only shows when the exported rows actually have a discount.
            ...(exportDiscountSummary.count > 0 ? [{ header: 'Discount', getValue: (b) => parseFloat(b.discount || 0), numeric: true }] : []),
            // Taxable/Total: red font when they don't reconcile against Basic/Discount/Tax,
            // green when they do — same check as the on-screen DataTable and the PDF.
            { header: 'Taxable', getValue: (b) => parseFloat(b.taxable || 0), numeric: true, getColor: (b) => (isTaxableCorrect(b) ? 'FF16A34A' : 'FFDC2626') },
            { header: 'Tax Amount', getValue: (b) => parseFloat(b.tax_amount || 0), numeric: true },
            { header: 'Total Amount', getValue: (b) => parseFloat(b.total_paid_amount || 0), numeric: true, getColor: (b) => (isTotalCorrect(b) ? 'FF16A34A' : 'FFDC2626') },
            { header: 'Balance', getValue: (b) => parseFloat(b.balance || 0), numeric: true },
            { header: 'MOP', getValue: (b) => b.final_mop || '-' },
            { header: 'Status', getValue: (b) => b.status || '-' }
        ];

        // Same left/right layout as the PDF header: Period+Branch on one line,
        // Invoice No range+Generated On on the next — with a light highlight fill
        // instead of the plain grey single-column lines other reports use.
        await exportStyledExcel({
            fileName: `BillReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Bill Report',
            title: 'Bill Report',
            filterLines: [
                organizationName ? { text: organizationName, bold: true, size: 12, center: true, fill: HIGHLIGHT_FILL } : null,
                { left: `Period: ${fromDate ? moment(fromDate).format('DD-MM-YYYY') : '-'} to ${toDate ? moment(toDate).format('DD-MM-YYYY') : '-'}`, right: `Branch: ${selectedBranch?.branch_name || 'All Branches'}`, bold: true, fill: HIGHLIGHT_FILL },
                { left: `Invoice No: ${invoiceRange.start || '-'} to ${invoiceRange.end || '-'}`, right: `Generated On: ${moment().format('DD-MM-YYYY hh:mm A')}`, bold: true, fill: HIGHLIGHT_FILL },
                exportDiscountSummary.count > 0
                    ? { left: `Discount: ₹${formatCurrency(exportDiscountSummary.amount)} (${exportDiscountSummary.count} bills)`, bold: true }
                    : null,
                exportNcSummary.count > 0 ? { left: `NC: ₹${formatCurrency(exportNcSummary.amount)} (${exportNcSummary.count} bills)`, bold: true } : null
            ],
            columns: billColumns,
            rows: exportRows,
            showFooterTotal: isCEO
        });
    };

    const grandTotal = filteredBillData.reduce((sum, item) => sum + parseFloat(item.total_paid_amount || 0), 0);

    // Discount/NC summary (count + amount), shown when their respective application_setting
    // toggle (DISCOUNT_BUTTON / NC) is enabled — computed per-context: the on-screen tags use
    // every filtered row, exports use whatever getExportRows() would export (selection-aware).
    const computeDiscountSummary = (rows) =>
        rows.reduce(
            (acc, row) => {
                const amt = parseFloat(row.discount || 0);
                if (amt > 0) {
                    acc.count += 1;
                    acc.amount += amt;
                }
                return acc;
            },
            { count: 0, amount: 0 }
        );
    const computeNcSummary = (rows) =>
        rows.reduce(
            (acc, row) => {
                if (normalizeMop(row.final_mop) === 'NC') {
                    acc.count += 1;
                    acc.amount += parseFloat(row.total_paid_amount || 0);
                }
                return acc;
            },
            { count: 0, amount: 0 }
        );
    const discountSummary = computeDiscountSummary(filteredBillData);

    // Chronological start (earliest) / end (most recent) invoice_no of the currently
    // filtered/displayed rows — independent of any export selection, shown as summary tags
    // instead of a separate export-only line. Rows come pre-sorted `invoice_date DESC,
    // bill_id DESC` (most recent first), so earliest is the *last* row, most recent the *first*.
    const displayedInvoiceRange = filteredBillData.length
        ? { start: filteredBillData[filteredBillData.length - 1]?.invoice_no || '', end: filteredBillData[0]?.invoice_no || '' }
        : { start: '', end: '' };

    /**
     * Branch Analysis: one row per distinct branch present in the currently filtered
     * data. Grouping by the data itself (rather than by the header's branch filter)
     * naturally satisfies both cases the report needs — a single branch selected in
     * the filter means every row already belongs to one branch (one group, one row),
     * while "All Branches" leaves multiple branches in filteredBillData (one row each).
     */
    const branchAnalysisRows = useMemo(() => buildBranchAnalysisRows(filteredBillData), [filteredBillData]);

    /** Colorful floating breakdown popup shown on hover over a Total Bills / Total Sales cell — { type: 'bills'|'sales', row, top, left }. */
    const [branchHover, setBranchHover] = useState(null);

    const showBranchBreakdown = (type, row) => (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setBranchHover({ type, row, top: rect.bottom + 8, left: rect.left });
    };
    const hideBranchBreakdown = () => setBranchHover(null);

    /**
     * Cancelled/NC/Credit/Discount split shared by both the Total Bills and Total Sales hover
     * cards; `value` picks the count or the amount for each category. A category with nothing
     * in it (count 0, so its amount is 0 too) is left out rather than shown as a bare zero.
     */
    const breakdownCategories = (row, value) =>
        [
            { key: 'cancelled', label: 'Cancelled', value: value(row.cancelledCount, row.cancelledAmount) },
            { key: 'nc', label: 'NC', value: value(row.ncCount, row.ncAmount) },
            { key: 'credit', label: 'Credit', value: value(row.creditCount, row.creditAmount) },
            { key: 'discount', label: 'Discount', value: value(row.discountCount, row.discountAmount) }
        ].filter((cat) => cat.value > 0);

    const branchAnalysisTotals = branchAnalysisRows.reduce(
        (acc, s) => {
            acc.totalBills += s.totalBills;
            acc.totalSales += s.totalSales;
            return acc;
        },
        { totalBills: 0, totalSales: 0 }
    );

    useEffect(() => {
        const searchTerm = globalFilter.trim().toLowerCase();
        const statusTerm = selectedStatusFilter ? selectedStatusFilter.toLowerCase() : null;
        const filtered = billData.filter((item) => {
            const matchesStatus = !statusTerm || (item.status && item.status.toLowerCase() === statusTerm);
            if (!matchesStatus) return false;
            if (!searchTerm) return true;
            return (
                (item.bill_id && item.bill_id.toString().toLowerCase().includes(searchTerm)) ||
                (item.invoice_no && item.invoice_no.toLowerCase().includes(searchTerm)) ||
                (item.final_mop && item.final_mop.toLowerCase().includes(searchTerm)) ||
                (item.status && item.status.toLowerCase().includes(searchTerm))
            );
        });
        setFilteredBillData(filtered);
    }, [globalFilter, selectedStatusFilter, billData]);

    const selectionCount = selectedRows.length;
    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const n = parseFloat(String(value).trim());
        return Number.isFinite(n) && n === 0;
    };

    /** "Billed By" = the logged-in user who processed the bill (userId -> users.username); waiter_no is the separate table-service waiter tagged on the bill, shown underneath when present. */
    const getBilledByName = (row) => {
        const name = (row?.waiter_name || '').trim();
        if (name) return name;
        if (row?.userId === null || row?.userId === undefined || String(row.userId).trim() === '') return '-';
        return `ID: ${row.userId}`;
    };

    const billedByBodyTemplate = (row) => {
        const waiterNo = row?.waiter_no === null || row?.waiter_no === undefined || String(row.waiter_no).trim() === '' ? null : row.waiter_no;
        if (!waiterNo) return getBilledByName(row);
        return (
            <div className="flex flex-column">
                <span>{getBilledByName(row)}</span>
                <span className="text-color-secondary text-sm">Waiter: {waiterNo}</span>
            </div>
        );
    };

    const getCustomerDisplay = (row) => {
        const name = (row?.cust_name || '').trim();
        return name || '-';
    };

    /** Date on top, time underneath, in one column instead of two side by side. */
    const dateTimeBodyTemplate = (row) => (
        <div className="flex flex-column">
            <span>{formatDate(row.invoice_date)}</span>
            <span className="text-color-secondary text-sm">{row.invoice_time || '-'}</span>
        </div>
    );

    /** created/updated body: same stacked date-over-time layout, with the invoice/edit-date fallback above. */
    const createdUpdatedBodyTemplate = (getter) => (row) => {
        const { date, time } = getter(row);
        return (
            <div className="flex flex-column">
                <span>{date}</span>
                {time && <span className="text-color-secondary text-sm">{time}</span>}
            </div>
        );
    };

    const AMOUNT_MATCH_TOLERANCE = 0.01;
    const amountsMatch = (a, b) => Math.abs(a - b) < AMOUNT_MATCH_TOLERANCE;

    /** Taxable should equal Basic - Discount. */
    const isTaxableCorrect = (row) => amountsMatch(parseFloat(row?.total_basic || 0) - parseFloat(row?.discount || 0), parseFloat(row?.taxable || 0));

    /** Total should equal Taxable + Tax. */
    const isTotalCorrect = (row) => amountsMatch(parseFloat(row?.taxable || 0) + parseFloat(row?.tax_amount || 0), parseFloat(row?.total_paid_amount || 0));

    const taxableCheckMessage = (row) => {
        const basic = parseFloat(row?.total_basic || 0);
        const discount = parseFloat(row?.discount || 0);
        const taxable = parseFloat(row?.taxable || 0);
        const expected = basic - discount;
        return isTaxableCorrect(row)
            ? `Matches: Basic ₹${formatCurrency(basic)} - Discount ₹${formatCurrency(discount)} = ₹${formatCurrency(expected)}`
            : `Mismatch: Basic ₹${formatCurrency(basic)} - Discount ₹${formatCurrency(discount)} = ₹${formatCurrency(expected)}, but Taxable shows ₹${formatCurrency(taxable)}`;
    };

    const totalCheckMessage = (row) => {
        const taxable = parseFloat(row?.taxable || 0);
        const tax = parseFloat(row?.tax_amount || 0);
        const total = parseFloat(row?.total_paid_amount || 0);
        const expected = taxable + tax;
        return isTotalCorrect(row)
            ? `Matches: Taxable ₹${formatCurrency(taxable)} + Tax ₹${formatCurrency(tax)} = ₹${formatCurrency(expected)}`
            : `Mismatch: Taxable ₹${formatCurrency(taxable)} + Tax ₹${formatCurrency(tax)} = ₹${formatCurrency(expected)}, but Total shows ₹${formatCurrency(total)}`;
    };

    /** Bold green when the reconciliation check passes, bold red when it doesn't; hover shows the calculation behind it. */
    const reconciledAmountBodyTemplate = (getValue, isCorrect, getMessage) => (row) => (
        <span className={isCorrect(row) ? 'text-green-600 font-bold' : 'text-red-600 font-bold'} title={getMessage(row)}>
            ₹{formatCurrency(getValue(row))}
        </span>
    );

    // Ordered to read left-to-right the way a report is naturally scanned:
    // identify the bill -> when -> who it involves -> the money -> how it was paid/settled.
    const billColumns = [
        // { field: 'bill_id', header: 'Bill ID', sortable: true, style: { minWidth: '80px' } },
        // { field: 'cust_name', header: 'Customer', sortable: true, style: { minWidth: '140px' }, body: getCustomerDisplay },
        { field: 'invoice_no', header: 'Invoice No', sortable: true, style: { minWidth: '140px' } },
        { field: 'invoice_date', header: 'Date / Time', sortable: true, style: { minWidth: '110px' }, body: dateTimeBodyTemplate },
        { field: 'waiter_name', header: 'Billed By', sortable: true, style: { minWidth: '150px' }, body: billedByBodyTemplate },
        { field: 'total_basic', header: 'Basic', sortable: true, style: { textAlign: 'right', minWidth: '100px' }, body: (row) => `₹${formatCurrency(row.total_basic)}` },
        // Discount hides when every row in view is 0 — same treatment as Tax below.
        { field: 'discount', header: 'Discount', style: { textAlign: 'right', minWidth: '90px' }, body: (row) => `₹${formatCurrency(row.discount)}`, hideIfAllZero: true },
        // Taxable/Total are colored green when they reconcile against Basic/Discount/Tax,
        // red when they don't — a quick visual flag for bad/inconsistent bill data.
        { field: 'taxable', header: 'Taxable', style: { textAlign: 'right', minWidth: '90px' }, body: reconciledAmountBodyTemplate((row) => row.taxable, isTaxableCorrect, taxableCheckMessage) },
        // Tax (GST) hides when every row in view is 0 — a client with no GST-liable items
        // shouldn't see an empty Tax column.
        { field: 'tax_amount', header: 'Tax', style: { textAlign: 'right', minWidth: '80px' }, body: (row) => `₹${formatCurrency(row.tax_amount)}`, hideIfAllZero: true },
        { field: 'total_paid_amount', header: 'Total', sortable: true, style: { textAlign: 'right', minWidth: '100px' }, body: reconciledAmountBodyTemplate((row) => row.total_paid_amount, isTotalCorrect, totalCheckMessage), hideIfAllZero: true },
        { field: 'balance', header: 'Balance', style: { textAlign: 'right', minWidth: '90px' }, body: (row) => `₹${formatCurrency(row.balance)}`, hideIfAllZero: true },
        { field: 'final_mop', header: 'MOP', style: { minWidth: '80px' } },
        { field: 'status', header: 'Status', sortable: true, style: { textAlign: 'center', minWidth: '100px' }, body: statusBodyTemplate },
        { field: 'created', header: 'Created', sortable: true, style: { minWidth: '110px' }, body: createdUpdatedBodyTemplate(getCreatedDateTime), hidden: true },
        { field: 'updated', header: 'Updated', sortable: true, style: { minWidth: '110px' }, body: createdUpdatedBodyTemplate(getUpdatedDateTime) }
    ];

    const visibleBillColumns = billColumns.filter((col) => {
        if (col.hidden) return false;
        if (!col.hideIfAllZero) return true;
        return !filteredBillData.every((row) => isZeroLike(row?.[col.field]));
    });

    const totalColumnIndex = visibleBillColumns.findIndex((col) => col.field === 'total_paid_amount');
    const billFooterGroup =
        isCEO && totalColumnIndex !== -1 ? (
            <ColumnGroup>
                <Row>
                    <Column footer="" />
                    {visibleBillColumns.map((col, index) => (
                        <Column
                            key={col.field}
                            footer={index === Math.max(0, totalColumnIndex - 1) ? 'Grand Total:' : index === totalColumnIndex ? `₹${formatCurrency(grandTotal)}` : ''}
                            style={index >= totalColumnIndex - 1 ? { textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' } : undefined}
                        />
                    ))}
                    <Column footer="" />
                </Row>
            </ColumnGroup>
        ) : null;

    const cancelDialogFooter = (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setCancelDialogVisible(false)} disabled={cancelSubmitting} />
            <Button label="Confirm cancel" icon="pi pi-ban" className="p-button-danger" onClick={submitCancelBills} loading={cancelSubmitting} />
        </>
    );

    const changeMopDialogFooter = (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setChangeMopDialogVisible(false)} disabled={changeMopSubmitting} />
            <Button
                label="Update payment mode"
                icon="pi pi-check"
                onClick={submitChangeMop}
                loading={changeMopSubmitting}
                disabled={changeMopSubmitting || changeMopLoading || !selectedNewMop || !changeMopRemarks.trim()}
            />
        </>
    );

    const settlementDialogFooter = (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setSettlementDialogVisible(false)} disabled={settlementSubmitting} />
            <Button
                label="Settle bills"
                icon="pi pi-check"
                onClick={submitSettlement}
                loading={settlementSubmitting}
                disabled={settlementSubmitting || settlementLoading || !settlementPaymentType || !settlementDate || !settlementRemarks.trim()}
            />
        </>
    );

    return (
        <div className="bill-reports-container">
            <Toast ref={toast} />

            <Dialog header="Cancel bill(s)" visible={cancelDialogVisible} style={{ width: 'min(480px, 95vw)' }} onHide={() => !cancelSubmitting && setCancelDialogVisible(false)} footer={cancelDialogFooter}>
                <p className="mt-0 text-color-secondary">
                    {selectionCount} bill(s) selected. Same mode of payment: <strong>{selectedRows[0]?.final_mop || '—'}</strong>
                </p>
                <label className="font-semibold block mb-2">
                    Select reason <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-column gap-2">
                    {cancelReasonOptions.map((option) => (
                        <div key={option.value} className="flex align-items-center gap-2">
                            <RadioButton inputId={`cancel-reason-${option.value}`} name="cancelReason" value={option.value} onChange={(e) => setCancelRemarks(e.value)} checked={cancelRemarks === option.value} />
                            <label htmlFor={`cancel-reason-${option.value}`}>{option.label}</label>
                        </div>
                    ))}
                </div>
            </Dialog>

            <Dialog header="Change mode of payment" visible={changeMopDialogVisible} style={{ width: 'min(480px, 95vw)' }} onHide={() => !changeMopSubmitting && setChangeMopDialogVisible(false)} footer={changeMopDialogFooter}>
                {changeMopLoading ? (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner style={{ width: 40, height: 40 }} />
                    </div>
                ) : (
                    <>
                        <p className="mt-0 text-color-secondary">
                            {selectionCount} bill(s). Current mode: <strong>{selectedRows[0]?.final_mop || '—'}</strong>. Choose a new mode (excluding the current one):
                        </p>
                        <Dropdown value={selectedNewMop} options={changeMopOptions} onChange={(e) => setSelectedNewMop(e.value)} placeholder="Select new payment mode" className="w-full" optionLabel="label" optionValue="value" />
                        {selectedNewMop && (
                            <div className="mt-3">
                                <label className="font-semibold block mb-2">Remarks</label>
                                <InputTextarea value={changeMopRemarks} onChange={(e) => setChangeMopRemarks(e.target.value)} rows={2} className="w-full" placeholder="Reason for changing the payment mode" />
                            </div>
                        )}
                    </>
                )}
            </Dialog>

            <Dialog header="Settlement (credit bills)" visible={settlementDialogVisible} style={{ width: 'min(520px, 95vw)' }} onHide={() => !settlementSubmitting && setSettlementDialogVisible(false)} footer={settlementDialogFooter}>
                {settlementLoading ? (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner style={{ width: 40, height: 40 }} />
                    </div>
                ) : (
                    <>
                        <p className="mt-0 text-color-secondary">
                            {selectionCount} credit bill(s). Each will be settled for its <strong>total paid amount</strong>.
                        </p>
                        <ul className="text-sm pl-3 mb-3" style={{ maxHeight: 120, overflow: 'auto' }}>
                            {selectedRows.map((r) => (
                                <li key={r.__selectionKey}>
                                    Bill {r.bill_id}: ₹{formatCurrency(r.total_paid_amount)}
                                </li>
                            ))}
                        </ul>
                        <div className="flex flex-column gap-3">
                            <div>
                                <label className="font-semibold block mb-2">Payment type</label>
                                <Dropdown
                                    value={settlementPaymentType}
                                    options={settlementPaymentOptions}
                                    onChange={(e) => setSettlementPaymentType(e.value)}
                                    placeholder="Select payment type"
                                    className="w-full"
                                    optionLabel="label"
                                    optionValue="value"
                                />
                            </div>
                            <div>
                                <label className="font-semibold block mb-2">Settlement date</label>
                                <Calendar value={settlementDate} onChange={(e) => setSettlementDate(e.value)} dateFormat="dd-mm-yy" showIcon className="w-full" showButtonBar />
                            </div>
                            <div>
                                <label className="font-semibold block mb-2">Remarks</label>
                                <InputTextarea value={settlementRemarks} onChange={(e) => setSettlementRemarks(e.target.value)} rows={2} className="w-full" placeholder="Reason for this settlement" />
                            </div>
                        </div>
                    </>
                )}
            </Dialog>

            {branchAnalysisRows.length > 0 && (
                <Card className="mb-3">
                    <div className="flex align-items-center gap-2 mb-3">
                        <h3 className="text-lg font-semibold m-0">Branch Analysis</h3>
                        <Tag value={`${branchAnalysisRows.length} Branch${branchAnalysisRows.length > 1 ? 'es' : ''}`} severity="info" />
                        <span className="text-color-secondary text-sm">Hover over Total Bills or Total Sales for a detailed breakdown</span>
                    </div>

                    <DataTable
                        value={branchAnalysisRows}
                        dataKey="branchId"
                        showGridlines
                        className="p-datatable-sm report-datatable-compact"
                        tableStyle={{ minWidth: '40rem' }}
                        footerColumnGroup={
                            branchAnalysisRows.length > 1 ? (
                                <ColumnGroup>
                                    <Row>
                                        <Column footer="Total" colSpan={2} style={{ textAlign: 'center', fontWeight: 'bold' }} />
                                        <Column footer={String(branchAnalysisTotals.totalBills)} style={{ textAlign: 'center', fontWeight: 'bold' }} />
                                        <Column footer={`₹${formatCurrency(branchAnalysisTotals.totalSales)}`} style={{ textAlign: 'center', fontWeight: 'bold' }} />
                                        <Column footer="" />
                                    </Row>
                                </ColumnGroup>
                            ) : null
                        }
                    >
                        <Column field="branchName" header="Branch" style={{ textAlign: 'center', minWidth: '160px' }} />
                        <Column
                            header="Invoice No"
                            style={{ textAlign: 'center', minWidth: '160px' }}
                            body={(row) =>
                                row.invoiceStart || row.invoiceEnd ? (
                                    <span
                                        className={`branch-breakdown-trigger branch-invoice-range ${row.invoiceHasGap ? 'branch-invoice-gap' : 'branch-invoice-ok'}`}
                                        onMouseEnter={showBranchBreakdown('invoice', row)}
                                        onMouseLeave={hideBranchBreakdown}
                                    >
                                        <span className="branch-stat-value">
                                            {row.invoiceStart || '-'} — {row.invoiceEnd || '-'}
                                        </span>
                                        <i className={`pi ${row.invoiceHasGap ? 'pi-exclamation-triangle' : 'pi-check-circle'} branch-breakdown-icon`} />
                                    </span>
                                ) : (
                                    '-'
                                )
                            }
                        />
                        <Column
                            header="Total Bills"
                            style={{ textAlign: 'center', minWidth: '110px' }}
                            body={(row) => (
                                <span className="branch-breakdown-trigger" onMouseEnter={showBranchBreakdown('bills', row)} onMouseLeave={hideBranchBreakdown}>
                                    <span className="branch-stat-value">{row.totalBills}</span>
                                    <i className="pi pi-chart-pie branch-breakdown-icon" />
                                </span>
                            )}
                        />
                        <Column
                            header="Total Sales"
                            style={{ textAlign: 'center', minWidth: '140px' }}
                            body={(row) => (
                                <span className="branch-breakdown-trigger" onMouseEnter={showBranchBreakdown('sales', row)} onMouseLeave={hideBranchBreakdown}>
                                    <span className="branch-stat-value branch-stat-sales">₹{formatCurrency(row.totalSales)}</span>
                                    <i className="pi pi-chart-pie branch-breakdown-icon" />
                                </span>
                            )}
                        />
                        <Column header="Avg Bill Value" style={{ textAlign: 'center', minWidth: '120px' }} body={(row) => `₹${formatCurrency(row.avgBillValue)}`} />
                    </DataTable>

                    {branchHover && branchHover.type === 'invoice' && (
                        <div
                            className={`branch-breakdown-popup ${branchHover.row.invoiceHasGap ? 'branch-invoice-popup-gap' : 'branch-invoice-popup-ok'}`}
                            style={{ top: branchHover.top, left: branchHover.left }}
                            onMouseEnter={() => setBranchHover(branchHover)}
                            onMouseLeave={hideBranchBreakdown}
                        >
                            <div className="breakdown-title">Invoice Sequence — {branchHover.row.branchName}</div>
                            {branchHover.row.invoiceHasGap ? (
                                <>
                                    <div className="breakdown-item breakdown-invoice-gap">
                                        <span className="breakdown-label">
                                            <span className="breakdown-dot" />
                                            Missing invoices
                                        </span>
                                        <span className="breakdown-value">{branchHover.row.invoiceMissingCount}</span>
                                    </div>
                                    <div className="breakdown-missing-list">
                                        {branchHover.row.invoiceMissingList.join(', ')}
                                        {branchHover.row.invoiceMissingCount > branchHover.row.invoiceMissingList.length && ', …'}
                                    </div>
                                </>
                            ) : (
                                <div className="breakdown-item breakdown-invoice-ok">
                                    <span className="breakdown-label">
                                        <span className="breakdown-dot" />
                                        Fully sequential — no gaps
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {branchHover && (branchHover.type === 'bills' || branchHover.type === 'sales') && (
                        <div
                            className="branch-breakdown-popup"
                            style={{ top: branchHover.top, left: branchHover.left }}
                            onMouseEnter={() => setBranchHover(branchHover)}
                            onMouseLeave={hideBranchBreakdown}
                        >
                            <div className="breakdown-title">
                                {branchHover.type === 'bills' ? 'Bills Breakdown' : 'Sales Breakdown'} — {branchHover.row.branchName}
                            </div>
                            {(() => {
                                const categories = breakdownCategories(branchHover.row, branchHover.type === 'bills' ? (count) => count : (_count, amount) => amount);
                                if (!categories.length) {
                                    return <div className="breakdown-empty">No Cancelled / NC / Credit / Discount bills — every bill is a clean sale.</div>;
                                }
                                return categories.map((cat) => (
                                    <div key={cat.key} className={`breakdown-item breakdown-${cat.key}`}>
                                        <span className="breakdown-label">
                                            <span className="breakdown-dot" />
                                            {cat.label}
                                        </span>
                                        <span className="breakdown-value">{branchHover.type === 'bills' ? cat.value : `₹${formatCurrency(cat.value)}`}</span>
                                    </div>
                                ));
                            })()}
                        </div>
                    )}
                </Card>
            )}

            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="flex align-items-center gap-2">
                        <h3 className="text-lg font-semibold m-0">Report Data</h3>
                        {filteredBillData.length > 0 && <Tag value={`${filteredBillData.length} Records`} severity="info" className="ml-2" />}
                        {discountSummary.count > 0 && (
                            <Tag value={`Discount: ₹${formatCurrency(discountSummary.amount)} (${discountSummary.count})`} severity="warning" />
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center">
                        <Dropdown
                            value={selectedStatusFilter}
                            options={statusFilterOptions}
                            onChange={(e) => setSelectedStatusFilter(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="All Statuses"
                            className="w-full sm:w-auto"
                            style={{ minWidth: '160px' }}
                        />
                        <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '220px' }}>
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search bills..." className="w-full" />
                        </span>

                        {canExport && (
                            <div
                                className="flex align-items-center gap-2 bill-export-actions"
                                title={selectedRows.length ? `Exports the ${selectedRows.length} selected bill(s)` : `Exports all ${filteredBillData.length} filtered record(s)`}
                            >
                                <Button label="Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={filteredBillData.length === 0} />
                                <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={filteredBillData.length === 0} />
                                <Button label="Print" icon="pi pi-print" className="p-button-secondary p-button-sm" onClick={printReport} disabled={filteredBillData.length === 0} />
                            </div>
                        )}
                    </div>
                </div>

                {selectionCount > 0 && (
                    <div className="bill-bulk-actions flex flex-wrap align-items-center gap-2 mb-3 p-3 border-round surface-border border-1 surface-50">
                        <span className="font-semibold mr-2">{selectionCount} selected</span>
                        <Button label="Cancel bill" icon="pi pi-times-circle" className="p-button-danger p-button-sm" onClick={openCancelDialog} />
                        <Button label="Change mode of payment" icon="pi pi-wallet" className="p-button-secondary p-button-sm" onClick={openChangeMopDialog} />
                        <Button label="Settlement" icon="pi pi-money-bill" className="p-button-sm" onClick={openSettlementDialog} />
                        <Button label="Clear selection" icon="pi pi-times" className="p-button-text p-button-sm ml-auto" onClick={() => setSelectedRows([])} />
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : filteredBillData.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6">
                        <i className="pi pi-inbox text-6xl text-color-secondary mb-3"></i>
                        <p className="text-xl text-color-secondary">No data available</p>
                        <p className="text-color-secondary mt-2">{billData.length === 0 ? 'Please apply filters to view bill reports' : 'No records match your search criteria'}</p>
                    </div>
                ) : (
                    <DataTable
                        value={tableRows}
                        selection={selectedRows}
                        onSelectionChange={(e) => setSelectedRows(e.value)}
                        dataKey="__selectionKey"
                        showGridlines
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        tableStyle={{ minWidth: '50rem' }}
                        className="p-datatable-sm report-datatable-compact"
                        stripedRows
                        emptyMessage="No bills found"
                        footerColumnGroup={billFooterGroup}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                        {visibleBillColumns.map((col) => (
                            <Column key={col.field} field={col.field} header={col.header} body={col.body} sortable={col.sortable} style={col.style} />
                        ))}
                        <Column header="Actions" body={actionBodyTemplate} style={{ textAlign: 'center', minWidth: '80px' }} />
                    </DataTable>
                )}
            </Card>
        </div>
    );
};

export default BillReportsContent;
